import { useEffect } from "react";
import { ChakraProvider } from "@chakra-ui/react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import theme from "./Themes";
import { setupPublicAxios, setupAuthAxios } from "./config/axios";
import { setupEtaHardLockAxiosInterceptor } from "./utils/setupEtaHardLockAxiosInterceptor";
import paths from "./utils/paths";
import globalConstants from "./utils/constants";
import Layout from "./components/Layout";
import { useBucketMethods } from "./components/SupplierInventorySearch/utils/bucketUtils";

import LoginPage from "./views/Login";
import PlaceOrderTest from "./views/PlaceOrderTest";
import TestOrder from "./views/TestOrder";
import Orders from "./views/Orders";
import Metrics from "./views/Metrics";
import Cars from "./views/Cars";
import SamePartOrders from "./views/SamePartOrders";
import Engine3D from "./views/Engine3D";
import UniqueCustomers from "./views/uniqueCustomers";
import PaidUnpaidPage from "./views/Orders/PaidUnpaidPage";
import AddCatalogPart from "./views/AddCatalogPart";
import { UserDetailsProvider } from "./context/userDetailsContext";
import SuprSendContext from "./context/suprSendContext";
import { ChatProvider } from "./context/chatContext";
import { ApiConfigProvider } from "./context/apiConfigContext";
import AvailabilityModal from "./components/SupplierInventorySearch/AvailabilityModal";
import {
  ApiAwareR2DOrders,
  ApiAwareDispatchedOrders,
  ApiAwareShipmentsDashboard,
} from "./components/ApiAwareComponents";
import ShipmentsDashboard from "./views/ShipmentsDashboard";
import { useGetOrderMediaConfig } from "./views/PlaceOrderTest/queryHooks";
import R2DOrders from "./views/R2DOrders";
import CBOOrders from "./views/CBOOrders";
import Reports from "./views/Reports";
import AnomalyDashboard from "./views/AnomalyDashboard";
import QCReview from "./views/QCReview";
import MissionControl from "./views/MissionControl";

setupPublicAxios(process.env.REACT_APP_BASE_URL);
setupEtaHardLockAxiosInterceptor();

export const App = () => {
  const { cleanupStaleSupplierDiscoveryBucketsForToday } = useBucketMethods();
  const { data: configData } = useGetOrderMediaConfig();
  const ff_use_lmd_package = configData?.ff_use_lmd_package
    ? JSON.parse(configData?.ff_use_lmd_package)
    : false;
  console.log(ff_use_lmd_package);
  useEffect(() => {
    if (localStorage.auth) {
      const authJSON = localStorage.getItem(globalConstants.AUTH_TOKEN);
      if (authJSON) {
        setupAuthAxios(process.env.REACT_APP_BASE_URL, authJSON);
      }
    }
    Notification.requestPermission();
    cleanupStaleSupplierDiscoveryBucketsForToday();
  }, []);

  return (
    <ChakraProvider theme={theme}>
      <ApiConfigProvider>
        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoutes>
                <Layout />
              </ProtectedRoutes>
            }
          >
            <Route index element={<Orders />} />
            <Route element={<Orders />} path="orders/:orderNo" />
            <Route element={<Metrics />} path={paths.metrics} />
            <Route element={<PlaceOrderTest />} path={paths.placeOrder} />
            <Route element={<TestOrder />} path={paths.testOrder} />
            <Route element={<Cars />} path={`${paths.cars}/*`}></Route>
            <Route element={<UniqueCustomers />} path={paths.customers} />
            <Route
              element={<SamePartOrders />}
              path={`${paths.samePartOrders}/*`}
            ></Route>
            <Route
              path={`${paths.engine3D}/*`}
              element={<Engine3D></Engine3D>}
            ></Route>
            <Route path={paths.unpaidOrders} element={<PaidUnpaidPage />} />
            <Route path={paths.r2dOrders} element={<ApiAwareR2DOrders />} />
            <Route
              path={paths.dispatchedOrders}
              element={<ApiAwareDispatchedOrders />}
            />
            <Route
              path={paths.shipmentsDashboard}
              element={<ApiAwareShipmentsDashboard />}
            />
            <Route
              path={paths.partAvailability}
              element={<AvailabilityModal />}
            />
            <Route path={paths.addCatalogPart} element={<AddCatalogPart />} />
            <Route path={paths.cboOrders} element={<CBOOrders />} />
            <Route path={paths.reports} element={<Reports />} />
            <Route
              path={paths.anomalyDashboard}
              element={<AnomalyDashboard />}
            />
            <Route path={paths.qcDashboard} element={<QCReview />} />
            <Route path={paths.missionControl} element={<MissionControl />} />
          </Route>
          <Route path={paths.login} element={<LoginPage />} />
        </Routes>
      </ApiConfigProvider>
    </ChakraProvider>
  );
};

function ProtectedRoutes({ children }: { children: JSX.Element }) {
  let location = useLocation();

  if (!localStorage.getItem(globalConstants.AUTH_TOKEN))
    return <Navigate to={paths.login} state={{ from: location }} replace />;

  return (
    <UserDetailsProvider>
      <SuprSendContext>
        <ChatProvider>{children}</ChatProvider>
      </SuprSendContext>
    </UserDetailsProvider>
  );
}
